// Minimap: a 2D-canvas read-only view of the simulation.
//
//   new Minimap({ canvas, world, player, renderer })
//     .render()                     compose one frame
//     .hitTest(px, py, clamp=false) canvas CSS pixels -> { x, y } world tiles, or null
//     .clampedHitTest(px, py)       same, but never null (drag-to-pan)
//     .pingAlert(x, y)              short-lived flashing marker at a world tile
//     .onAlert = (alert, offScreen) => {}   optional hook; the minimap drains
//                                   fog's under-attack feed, so anything else
//                                   that wants it (a HUD banner, a sound) has
//                                   to read it here rather than call
//                                   drainAlerts() and race for the same events.
//
// LAYERING, bottom to top:
//   1. terrain      static, painted ONCE into a 96x96 offscreen canvas and
//                   blitted with smoothing off. Repainting 9216 tiles every
//                   frame at 60 Hz would cost more than the entire simulation.
//   2. blips        resources and live entities
//   3. fog          a 96x96 RGBA overlay of pure black at three alphas, rebuilt
//                   only when fog.version changes (12 Hz, not 60)
//   4. ghosts       remembered enemy structures
//   5. viewport     the camera rectangle
//   6. pings        under-attack alerts
//
// Live blips are drawn UNDER the fog on purpose: "enemy units only where
// currently visible" then falls out of the fog itself rather than needing a
// second visibility test, and resources correctly dim in explored territory.
// Remembered structures go ON TOP in a muted enemy tone -- putting them under
// the fog too is technically tidier and reads terribly, because the one thing
// you go and scout for ends up the dimmest mark on the map.
//
// COORDINATE CONTRACT: hitTest() takes pixels in the canvas element's CSS box
// (what `ev.clientX - canvas.getBoundingClientRect().left` gives you) and
// returns FRACTIONAL tile coordinates. The device-pixel backing store is an
// internal detail; the minimap owns it.

import { FOG_UNEXPLORED, FOG_EXPLORED, FOG_VISIBLE } from '../sim/constants.js';
import { canSee, drainAlerts } from '../sim/fog.js';

// ---------------------------------------------------------------------------
// palette -- exported so the 3D renderer and HUD can agree on team identity
// ---------------------------------------------------------------------------
export const TEAM_COLORS = ['#4ea3ff', '#ff4b3e'];

export const MINIMAP_COLORS = {
  void:      '#05070a',
  low:       [58, 74, 56],
  high:      [104, 120, 82],
  ramp:      [142, 126, 84],
  cliff:     [26, 28, 31],
  border:    [14, 15, 17],
  mineral:   '#6fe0ff',
  geyser:    '#35d67a',
  ghost:     '#a03a32',      // remembered enemy structure: muted enemy red
  viewport:  'rgba(255,255,255,0.92)',
  ghostCore: '#3a1f1d',      // hollows the ghost out so it never reads as live
  ping:      '#ff3b30',
};

/** Alpha of the black fog overlay for each of the three tile states. */
export function fogAlpha(state) {
  if (state === FOG_VISIBLE) return 0;
  if (state === FOG_EXPLORED) return 142;
  return 255;
}

// ---------------------------------------------------------------------------
// pure geometry -- no canvas, no DOM, fully unit-testable
// ---------------------------------------------------------------------------

/**
 * Letterbox a mapW x mapH square into a canvasW x canvasH backing store.
 * Offsets are kept fractional so pixelToTile is an exact inverse of tileToPixel;
 * rounding them would make a click land up to half a tile away from where the
 * viewport rectangle was drawn.
 */
export function minimapLayout(canvasW, canvasH, mapW, mapH) {
  const cw = Math.max(1, canvasW), ch = Math.max(1, canvasH);
  const scale = Math.min(cw / mapW, ch / mapH);
  const w = mapW * scale, h = mapH * scale;
  return { cw, ch, scale, w, h, ox: (cw - w) / 2, oy: (ch - h) / 2, mapW, mapH };
}

/** Backing-store pixel -> fractional tile. null when the point misses the map. */
export function pixelToTile(layout, px, py) {
  const x = (px - layout.ox) / layout.scale;
  const y = (py - layout.oy) / layout.scale;
  if (x < 0 || y < 0 || x >= layout.mapW || y >= layout.mapH) return null;
  return { x, y };
}

/** Same, clamped to the map. Used while dragging so the camera keeps following. */
export function pixelToTileClamped(layout, px, py) {
  const e = 1e-4;
  const x = Math.min(layout.mapW - e, Math.max(0, (px - layout.ox) / layout.scale));
  const y = Math.min(layout.mapH - e, Math.max(0, (py - layout.oy) / layout.scale));
  return { x, y };
}

/** Fractional tile -> backing-store pixel. */
export function tileToPixel(layout, x, y) {
  return { px: layout.ox + x * layout.scale, py: layout.oy + y * layout.scale };
}

/**
 * Project the camera's world rect onto the minimap and clip it to the map box.
 * Returns null when the camera is looking entirely off-map.
 */
export function projectViewRect(layout, rect) {
  if (!rect) return null;
  const { scale, ox, oy, w, h } = layout;
  let x0 = ox + Math.min(rect.x0, rect.x1) * scale;
  let x1 = ox + Math.max(rect.x0, rect.x1) * scale;
  let y0 = oy + Math.min(rect.y0, rect.y1) * scale;
  let y1 = oy + Math.max(rect.y0, rect.y1) * scale;
  x0 = Math.max(ox, x0); y0 = Math.max(oy, y0);
  x1 = Math.min(ox + w, x1); y1 = Math.min(oy + h, y1);
  if (!(x1 > x0) || !(y1 > y0)) return null;
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

/** True if a world point sits inside the camera rect, with a tile of slack. */
export function rectContains(rect, x, y, pad = 0) {
  if (!rect) return false;
  return x >= Math.min(rect.x0, rect.x1) - pad && x <= Math.max(rect.x0, rect.x1) + pad
      && y >= Math.min(rect.y0, rect.y1) - pad && y <= Math.max(rect.y0, rect.y1) + pad;
}

/** Terrain tone for one tile index, as [r,g,b]. */
export function terrainTone(grid, i) {
  const C = MINIMAP_COLORS;
  if (grid.ramp[i]) return C.ramp;
  if (grid.cliff[i]) return grid.cliff[i] === 2 ? C.border : C.cliff;
  return grid.height[i] ? C.high : C.low;
}

// ---------------------------------------------------------------------------

const PING_MS = 2600;
const PING_BLINK_MS = 130;

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

function defaultCreateCanvas(w, h) {
  if (typeof OffscreenCanvas === 'function') return new OffscreenCanvas(w, h);
  const doc = globalThis.document;
  if (!doc || typeof doc.createElement !== 'function') return null;
  const c = doc.createElement('canvas');
  c.width = w; c.height = h;
  return c;
}

export class Minimap {
  constructor({ canvas, world, player = 0, renderer = null, createCanvas = defaultCreateCanvas }) {
    this.canvas = canvas || null;
    this.world = world;
    this.player = player;
    this.renderer = renderer;
    this.grid = world.grid;
    this.mapW = this.grid.w;
    this.mapH = this.grid.h;

    // A missing canvas must not take the whole game down with it: the minimap
    // is one HUD panel, and an unplayable black screen is a far worse failure
    // than a blank minimap. render() and hitTest() both no-op without a context.
    this.ctx = canvas && typeof canvas.getContext === 'function' ? canvas.getContext('2d') : null;
    this._createCanvas = createCanvas;

    // layer 1: terrain, one pixel per tile, painted once
    // layer 3: fog, one pixel per tile, refreshed on fog.version
    this.terrainCanvas = this.ctx ? createCanvas(this.mapW, this.mapH) : null;
    this.fogCanvas = this.ctx ? createCanvas(this.mapW, this.mapH) : null;
    this.fogCtx = this.fogCanvas ? this.fogCanvas.getContext('2d') : null;
    if (!this.terrainCanvas || !this.fogCtx) {
      // no offscreen surfaces available -- disable drawing, keep hit testing
      this.ctx = null; this.terrainCanvas = null; this.fogCanvas = null; this.fogCtx = null;
    } else {
      this._paintTerrain();
      this.fogImage = this.fogCtx.createImageData(this.mapW, this.mapH);
    }
    this._fogVersion = -1;

    this.pings = [];
    /** Optional hook for the HUD: called with every under-attack alert fog raises. */
    this.onAlert = null;
    this._cssW = -1; this._cssH = -1;
    this.dprX = 1; this.dprY = 1;
    this.layout = minimapLayout((canvas && canvas.width) || 1, (canvas && canvas.height) || 1, this.mapW, this.mapH);

    // reusable blip buckets; allocating these per frame at 60 Hz is pure garbage
    this._buckets = new Map();
    this._ghosts = [];
  }

  // -- terrain ------------------------------------------------------------
  _paintTerrain() {
    const g = this.grid, n = this.mapW * this.mapH;
    const ctx = this.terrainCanvas.getContext('2d');
    const img = ctx.createImageData(this.mapW, this.mapH);
    const d = img.data;
    for (let i = 0; i < n; i++) {
      const c = terrainTone(g, i);
      const o = i * 4;
      d[o] = c[0]; d[o + 1] = c[1]; d[o + 2] = c[2]; d[o + 3] = 255;
    }
    ctx.putImageData(img, 0, 0);
  }

  // -- fog ----------------------------------------------------------------
  _refreshFog() {
    const f = this.world.fog[this.player];
    const v = f.version ?? this.world.tick;
    if (v === this._fogVersion) return;
    this._fogVersion = v;
    const vis = f.vis, d = this.fogImage.data, n = vis.length;
    // r=g=b stay 0 from createImageData; only the alpha byte ever changes.
    for (let i = 0; i < n; i++) {
      const s = vis[i];
      d[i * 4 + 3] = s === FOG_VISIBLE ? 0 : s === FOG_EXPLORED ? 142 : 255;
    }
    this.fogCtx.putImageData(this.fogImage, 0, 0);
  }

  // -- sizing -------------------------------------------------------------
  /**
   * Keep the backing store at CSS size x devicePixelRatio, but bail out if the
   * canvas has no CSS box: writing .width to such an element changes its layout
   * size, which would otherwise feed back and double the resolution every frame.
   */
  _sync() {
    const c = this.canvas;
    if (!c) return;
    const cw = c.clientWidth | 0, ch = c.clientHeight | 0;
    if (cw > 0 && ch > 0 && (cw !== this._cssW || ch !== this._cssH)) {
      this._cssW = cw; this._cssH = ch;
      const dpr = Math.min(3, Math.max(1, globalThis.devicePixelRatio || 1));
      const wantW = Math.max(1, Math.round(cw * dpr));
      const wantH = Math.max(1, Math.round(ch * dpr));
      if (c.width !== wantW || c.height !== wantH) {
        c.width = wantW; c.height = wantH;
        if ((c.clientWidth | 0) !== cw || (c.clientHeight | 0) !== ch) {
          // the element is sized BY its attributes -- undo and leave it alone
          c.width = cw; c.height = ch;
          this._cssW = c.clientWidth | 0; this._cssH = c.clientHeight | 0;
        }
      }
    }
    const dw = c.clientWidth || c.width || 1;
    const dh = c.clientHeight || c.height || 1;
    this.dprX = (c.width || 1) / dw;
    this.dprY = (c.height || 1) / dh;
    if (this.layout.cw !== (c.width || 1) || this.layout.ch !== (c.height || 1)) {
      this.layout = minimapLayout(c.width || 1, c.height || 1, this.mapW, this.mapH);
    }
  }

  resize() { this._cssW = -1; this._cssH = -1; this._sync(); }

  // -- input --------------------------------------------------------------
  /**
   * Canvas CSS pixels -> world tile, or null if the point missed the map.
   *
   * Pass `clamp` while a drag is in progress. A minimap drag in StarCraft keeps
   * panning while the pointer runs off the panel and only stops on mouse-up;
   * returning null at the letterbox edge instead would make the camera stall
   * the instant the pointer overshoots, which feels like the game dropped input.
   */
  hitTest(px, py, clamp = false) {
    if (!this.canvas) return null;
    this._sync();
    const x = px * this.dprX, y = py * this.dprY;
    return clamp ? pixelToTileClamped(this.layout, x, y) : pixelToTile(this.layout, x, y);
  }
  /** Same but never null -- use this while dragging. */
  clampedHitTest(px, py) { return this.hitTest(px, py, true) || { x: 0, y: 0 }; }

  /** A short-lived flashing marker. Called by the alert feed and by the HUD. */
  pingAlert(x, y, color = MINIMAP_COLORS.ping) {
    this.pings.push({ x, y, t0: now(), color });
    if (this.pings.length > 8) this.pings.shift();
  }

  // -- frame --------------------------------------------------------------
  render() {
    const c = this.canvas;
    if (!this.ctx || !c) return;
    this._sync();
    const ctx = this.ctx, L = this.layout;
    const cw = c.width || 1, ch = c.height || 1;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.imageSmoothingEnabled = false;
    ctx.globalAlpha = 1;
    ctx.fillStyle = MINIMAP_COLORS.void;
    ctx.fillRect(0, 0, cw, ch);

    ctx.drawImage(this.terrainCanvas, L.ox, L.oy, L.w, L.h);

    this._collectBlips();
    this._drawBuckets(ctx);

    this._refreshFog();
    ctx.drawImage(this.fogCanvas, L.ox, L.oy, L.w, L.h);

    this._drawGhosts(ctx);
    this._pumpAlerts();
    this._drawViewport(ctx, L);
    this._drawPings(ctx, L);
  }

  // -- blips --------------------------------------------------------------
  _bucket(color) {
    let b = this._buckets.get(color);
    if (!b) { b = []; this._buckets.set(color, b); }
    return b;
  }

  _rect(out, wx, wy, tiles) {
    const L = this.layout;
    const s = Math.max(2, Math.round(tiles * L.scale));
    out.push(L.ox + wx * L.scale - s / 2, L.oy + wy * L.scale - s / 2, s);
  }

  _collectBlips() {
    for (const b of this._buckets.values()) b.length = 0;
    const w = this.world, me = this.player, foe = 1 - me;
    const C = MINIMAP_COLORS;

    // Buckets are resolved once per group, not once per entity: at 60 Hz with a
    // 400-supply game on the board this loop runs ~24000 times a second and a
    // hash lookup per blip is pure overhead for a value that never changes.
    const minerals = this._bucket(C.mineral), geysers = this._bucket(C.geyser);
    for (const id of w.resources) {
      const r = w.entities.get(id);
      if (!r || !r.alive) continue;
      // resources are drawn UNDER the fog; explored-but-dark ones dim correctly
      this._rect(r.defId === 'geyser' ? geysers : minerals, r.x, r.y, Math.max(r.w, r.h));
    }

    // remembered enemy structures, only where we cannot currently see the lot
    this._ghosts.length = 0;
    for (const m of w.fog[me].memory.values()) {
      const live = w.entities.get(m.id);
      if (live && live.alive && canSee(w, me, live)) continue;
      this._rect(this._ghosts, m.x, m.y, Math.max(m.w, m.h));
    }

    // enemy, only where currently visible (cloak included, via canSee)
    const foes = this._bucket(TEAM_COLORS[foe] || '#ff4b3e');
    for (const id of w.byPlayer[foe]) {
      const e = w.entities.get(id);
      if (!e || !e.alive || !canSee(w, me, e)) continue;
      this._rect(foes, e.x, e.y, e.kind === 'building' ? Math.max(e.w, e.h) : 1);
    }

    // own forces last so they are never hidden behind anything
    const own = this._bucket(TEAM_COLORS[me] || '#4ea3ff');
    for (const id of w.byPlayer[me]) {
      const e = w.entities.get(id);
      if (!e || !e.alive) continue;
      this._rect(own, e.x, e.y, e.kind === 'building' ? Math.max(e.w, e.h) : 1);
    }
  }

  _drawBuckets(ctx) {
    for (const [color, b] of this._buckets) {
      if (!b.length) continue;
      ctx.fillStyle = color;
      for (let i = 0; i < b.length; i += 3) ctx.fillRect(b[i], b[i + 1], b[i + 2], b[i + 2]);
    }
  }

  /** Above the fog, hollow, so a ghost never reads as a live building. */
  _drawGhosts(ctx) {
    const b = this._ghosts;
    if (!b.length) return;
    ctx.globalAlpha = 1;
    ctx.fillStyle = MINIMAP_COLORS.ghost;
    for (let i = 0; i < b.length; i += 3) ctx.fillRect(b[i], b[i + 1], b[i + 2], b[i + 2]);
    if (this.layout.scale >= 2.5) {
      ctx.fillStyle = MINIMAP_COLORS.ghostCore;
      for (let i = 0; i < b.length; i += 3) {
        const s = b[i + 2];
        if (s < 4) continue;
        ctx.fillRect(b[i] + 1, b[i + 1] + 1, s - 2, s - 2);
      }
    }
  }

  // -- viewport -----------------------------------------------------------
  _viewRect() {
    const cam = this.renderer && this.renderer.camera;
    if (!cam || typeof cam.viewRect !== 'function') return null;
    try { return cam.viewRect(); } catch { return null; }
  }

  _drawViewport(ctx, L) {
    const r = projectViewRect(L, this._viewRect());
    if (!r) return;
    const lw = Math.max(1, Math.round(L.scale * 0.5));
    ctx.lineWidth = lw;
    ctx.strokeStyle = 'rgba(0,0,0,0.55)';
    ctx.strokeRect(r.x - lw, r.y - lw, r.w + lw * 2, r.h + lw * 2);
    ctx.strokeStyle = MINIMAP_COLORS.viewport;
    ctx.strokeRect(r.x + lw / 2, r.y + lw / 2, Math.max(lw, r.w - lw), Math.max(lw, r.h - lw));
  }

  // -- alerts / pings -----------------------------------------------------
  /**
   * Turn fog's under-attack feed into pings. Only fires for things happening
   * off-screen: BW does not flash the minimap for a fight you are already
   * watching, and neither should this.
   */
  _pumpAlerts() {
    const alerts = drainAlerts(this.world, this.player);
    if (!alerts.length) return;
    const view = this._viewRect();
    for (const a of alerts) {
      const offScreen = !rectContains(view, a.x, a.y, -1.5);
      if (offScreen) this.pingAlert(a.x, a.y);
      if (this.onAlert) this.onAlert(a, offScreen);
    }
  }

  _drawPings(ctx, L) {
    if (!this.pings.length) return;
    const t = now();
    let write = 0;
    for (let i = 0; i < this.pings.length; i++) {
      const p = this.pings[i];
      const age = t - p.t0;
      if (age >= PING_MS) continue;
      this.pings[write++] = p;

      if (Math.floor(age / PING_BLINK_MS) % 2 === 1) continue;   // blink
      const k = age / PING_MS;
      const base = Math.max(6, L.scale * 5);
      const size = base * (1 + k * 0.9);
      const px = L.ox + p.x * L.scale, py = L.oy + p.y * L.scale;
      ctx.globalAlpha = 1 - k * 0.7;
      ctx.strokeStyle = p.color;
      ctx.lineWidth = Math.max(1, Math.round(L.scale * 0.6));
      ctx.strokeRect(px - size / 2, py - size / 2, size, size);
      ctx.fillStyle = p.color;
      const dot = Math.max(2, Math.round(L.scale));
      ctx.fillRect(px - dot / 2, py - dot / 2, dot, dot);
    }
    this.pings.length = write;
    ctx.globalAlpha = 1;
  }
}

export default Minimap;
